import React from "react";
import { EvidencingFragment } from "~/graphql/codegen/graphql";
import boltGradientSvg from "~/assets/bolt-gradient.svg";
import useEvidencing from "../hooks/evidencing";
import { Button } from "@mindstonehq/ui";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

export default function PointsPanel({
  close,
  article,
  pointsToShow = "earned",
}: {
  article: {
    id: string;
    evidencing: EvidencingFragment;
  };
  close: () => void;
  pointsToShow?: "earned" | "earnable";
}) {
  const { t } = useLingui();
  const evidencing = useEvidencing(article.id, article.evidencing);

  const pointsInfoToShow =
    (!!evidencing.totalEarnablePoints && pointsToShow === "earnable") ||
    (!!evidencing.totalPassiveEarnedPoints && pointsToShow === "earned");

  return (
    <div className="h-full flex flex-col">
      <div className="p-6 flex items-center justify-between border-b border-border bg-background">
        <h5><Trans>Understanding Your ⚡ Evidence Points (EP)</Trans></h5>
      </div>
      <div className="py-4 flex flex-col grow overflow-auto">
        {!pointsInfoToShow && (
          <div className="flex flex-col items-center justify-center mt-2">
            <img src={boltGradientSvg} alt={t(msg`points`)} className="h-8 w-8 mt-4 mb-2 w-full text-center" />
            <div className="mb-8">
              {pointsToShow === "earnable"
                ? t(msg`Points information will be ready for this resource later`)
                : t(msg`No points earned yet on this resource`)}
            </div>
          </div>
        )}
        <div className="text-muted-foreground mb-4 px-4 ">
          <p>
            <Trans>Each learning action you take - like reading articles, taking notes, acing trivia, and more - earns you Evidence Points towards relevant skills your resource is helping you build.</Trans>
          </p>
          <p>
            <Button variant="link" asChild>
              <a
                href="https://help.mindstone.com/what-are-evidence-points"
                className="flex items-center gap-2"
                target="_blank"
              >
                <InformationCircleIcon className="h-5 w-5" />
                <Trans>How are Evidence Points calculated?</Trans>
              </a>
            </Button>
          </p>
        </div>
        {pointsInfoToShow && (
          <>
            <div className="py-3 text-base font-bold text-center px-4">
              <Trans>Total:</Trans>{" "}
              {Math.round(
                pointsToShow === "earnable"
                  ? evidencing.totalPassiveEarnablePoints
                  : evidencing.totalPassiveEarnedPoints,
              )}
            </div>
            <div className="divide-y divide-solid divide-border grow overflow-y-auto px-2 md:px-0">
              {evidencing.passiveDetails.map((detail) => (
                <div key={detail.skill.name} className="flex items-center justify-between h-12 px-4">
                  <div className="flex items-center">
                    <img src={boltGradientSvg} alt={t(msg`points`)} className="h-4 w-4" />
                    {Math.ceil(pointsToShow === "earnable" ? detail.earnablePoints : detail.earnedPoints)}
                  </div>
                  <div className="text-muted-foreground w-[250px] text-left md:text-right truncate">
                    {detail.skill.name}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
